import { OpenTabState, OpenTabSummaryFragment } from '@/features/graphql/generated/graphql'
import { useLicence } from '@/features/licence/hooks/useLicence'
import { useFirebaseOpenTabs } from '@/features/openTabs/hooks/useFirebaseOpenTabs'
import { useOpenTabDetail } from '@/features/openTabs/hooks/useOpenTabDetail'
import { logger } from '@/features/openTabs/logger'
import { OpenTabsDocument } from '@/features/openTabs/queries'
import {
    AccountStatus,
    FirebaseOpenTabSummary,
    OpenTabAccount,
    OpenTabsValue,
} from '@/features/openTabs/types'
import { getAccountsTotal } from '@/features/openTabs/utils/accountCalculations'
import {
    isRenderableOpenTab,
    toAccountDetail,
    toFirebaseAccountSummary,
    toPaymentMethods,
} from '@/features/openTabs/utils/backendTransforms'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useQuery } from '@apollo/client'
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

type AccountRecordsValue = Omit<
    OpenTabsValue,
    'creatingAccount' | 'setActiveStatus' | 'selectAccount' | 'clearSelection' | 'createAccount'
>

const useAccountRecords = (settleAccountInFlightRef: RefObject<boolean>) => {
    const handleError = useHandleErrorWithToast(logger)
    const { currency } = useLicence()
    const source = useFirebaseOpenTabs()
    const settings = useQuery(OpenTabsDocument, { fetchPolicy: 'cache-and-network' })
    const [activeStatus, setActiveStatusState] = useState<AccountStatus>('open')
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
    const [pendingCreatedTabs, setPendingCreatedTabs] = useState<
        Record<string, FirebaseOpenTabSummary>
    >({})
    const sourceRevisionsRef = useRef(source.revisions)
    sourceRevisionsRef.current = source.revisions
    const detail = useOpenTabDetail(
        selectedAccountId,
        selectedAccountId ? source.revisions[selectedAccountId] : undefined
    )
    useEffect(() => {
        if (settings.error) void handleError(settings.error)
    }, [handleError, settings.error])

    useEffect(() => {
        setPendingCreatedTabs((previous) => {
            const records = Object.entries(previous).filter(
                ([uuid]) => source.revisions[uuid] === undefined
            )
            return records.length === Object.keys(previous).length
                ? previous
                : Object.fromEntries(records)
        })
    }, [source.revisions])
    const rememberCreatedTab = useCallback((tab: OpenTabSummaryFragment) => {
        if (sourceRevisionsRef.current[tab.uuid] !== undefined) return
        setPendingCreatedTabs((previous) => ({
            ...previous,
            [tab.uuid]: {
                uuid: tab.uuid,
                name: tab.name,
                state: 'open',
                createdAt: String(tab.createdAt),
                totalPrice: tab.totalPrice,
                unsettledPrice: tab.unsettledPrice,
                settlements: tab.settlements.map((settlement) => ({
                    uuid: settlement.uuid,
                    createdAt: String(settlement.createdAt),
                    orderUuid: settlement.order.uuid,
                    orderTotalPrice: settlement.order.totalPrice,
                })),
            },
        }))
    }, [])
    const records = useMemo(() => {
        const pending = Object.values(pendingCreatedTabs).filter(
            (tab) => source.revisions[tab.uuid] === undefined
        )
        return pending.length ? [...source.tabs, ...pending] : source.tabs
    }, [pendingCreatedTabs, source.tabs, source.revisions])
    const numbers = useMemo(
        () =>
            new Map(
                records
                    .filter(({ state }) => state !== 'cancelled')
                    .sort(
                        (left, right) =>
                            Date.parse(left.createdAt) - Date.parse(right.createdAt) ||
                            left.uuid.localeCompare(right.uuid)
                    )
                    .map((tab, index) => [tab.uuid, index + 1])
            ),
        [records]
    )
    const summaryAccounts = useMemo(() => {
        if (!currency) return []

        return records.flatMap((tab) => {
            const number = numbers.get(tab.uuid)
            if (!number) return []
            return [toFirebaseAccountSummary(tab, number, currency)]
        })
    }, [currency, numbers, records])
    const selectedRecord = records.find(({ uuid }) => uuid === selectedAccountId)
    const selectedSummary = summaryAccounts.find(({ id }) => id === selectedAccountId)
    const selectedAccountCancelled =
        selectedRecord?.state === 'cancelled' ||
        (!!selectedAccountId &&
            detail.tab?.uuid === selectedAccountId &&
            detail.tab.state === OpenTabState.Cancelled)
    const resolvedSelectedAccount = useMemo(() => {
        if (!currency || !selectedRecord || selectedRecord.state === 'cancelled') return undefined
        if (detail.tab?.uuid !== selectedAccountId || !isRenderableOpenTab(detail.tab))
            return undefined
        const account = toAccountDetail(detail.tab, numbers.get(selectedRecord.uuid) ?? 0)
        return {
            ...account,
            ...selectedSummary,
            name: selectedRecord.name,
            status: selectedRecord.state,
            openedAt: selectedRecord.createdAt,
            unsettledAmount:
                selectedRecord.state === 'closed'
                    ? 0
                    : toFirebaseAccountSummary(selectedRecord, account.number, currency)
                          .unsettledAmount,
        }
    }, [currency, detail.tab, numbers, selectedAccountId, selectedRecord, selectedSummary])
    const retainedSelection = useRef<OpenTabAccount | undefined>(undefined)
    if (!settleAccountInFlightRef.current || retainedSelection.current?.id !== selectedAccountId)
        retainedSelection.current = resolvedSelectedAccount
    const selectedAccount =
        settleAccountInFlightRef.current && retainedSelection.current?.id === selectedAccountId
            ? retainedSelection.current
            : resolvedSelectedAccount

    const selectedAccountSummary = selectedAccount ?? selectedSummary

    const openTabs = useMemo(
        () =>
            summaryAccounts
                .filter(({ status }) => status === 'open')
                .sort((left, right) => left.number - right.number),
        [summaryAccounts]
    )
    const closedAccounts = useMemo(
        () =>
            summaryAccounts
                .filter(({ status }) => status === 'closed')
                .sort(
                    (left, right) =>
                        Date.parse(right.closedAt ?? right.openedAt) -
                            Date.parse(left.closedAt ?? left.openedAt) ||
                        left.id.localeCompare(right.id)
                ),
        [summaryAccounts]
    )
    const paymentMethods = useMemo(
        () => toPaymentMethods(settings.data?.deliveryPaymentMappings ?? []),
        [settings.data?.deliveryPaymentMappings]
    )
    const openTabsTotal = useMemo(() => getAccountsTotal(openTabs), [openTabs])
    const closedAccountsTotal = useMemo(() => getAccountsTotal(closedAccounts), [closedAccounts])
    const { refresh: refetchSummaries, retry: retrySource, failed: sourceFailed } = source
    const { refetch: refetchDetail, retry: retryDetail } = detail
    const { refetch: refetchSettings } = settings
    const refreshAccount = useCallback(async () => {
        await Promise.all([refetchSummaries(), refetchDetail()])
    }, [refetchDetail, refetchSummaries])
    const retry = useCallback(() => {
        if (sourceFailed) retrySource()
        else void refetchSummaries().catch(() => {})
        retryDetail()
        void refetchSettings().catch(() => {})
    }, [refetchSettings, refetchSummaries, retryDetail, retrySource, sourceFailed])
    const sourceReady = !source.loading && !source.failed
    const detailLoading = detail.loading || (!!selectedAccountId && source.loading)
    const detailFailed =
        !!selectedAccountId &&
        (!selectedRecord || selectedAccountCancelled || (detail.failed && !detail.tab))
    const detailRefreshFailed =
        !!selectedAccountId && (source.failed || (detail.failed && !!detail.tab))
    const value = useMemo<AccountRecordsValue>(
        () => ({
            accounts: summaryAccounts,
            openTabs,
            closedAccounts,
            activeStatus,
            selectedAccountId,
            selectedAccount,
            selectedAccountSummary,
            currency,
            loading: source.loading || (settings.loading && !settings.data),
            detailLoading,
            listFailed: source.failed || !!settings.error || (!settings.loading && !currency),
            detailFailed,
            detailRefreshFailed,
            openTabsTotal,
            closedAccountsTotal,
            paymentMethods,
            retry,
            refreshAccount,
        }),
        [
            activeStatus,
            closedAccounts,
            closedAccountsTotal,
            currency,
            detailFailed,
            detailLoading,
            detailRefreshFailed,
            openTabs,
            openTabsTotal,
            refreshAccount,
            retry,
            selectedAccount,
            selectedAccountId,
            selectedAccountSummary,
            settings.data,
            settings.error,
            settings.loading,
            paymentMethods,
            source.failed,
            source.loading,
            summaryAccounts,
        ]
    )

    return {
        value,
        sourceReady,
        selectedRecord,
        summaryAccounts,
        detailAccountId: detail.tab?.uuid,
        selectedAccountCancelled,
        refetchSummaries,
        refetchDetail,
        refetchSettings,
        rememberCreatedTab,
        setActiveStatusState,
        setSelectedAccountId,
    }
}
export default useAccountRecords
